// Nómina · planilla interna.
//
// El cálculo no vive aquí: viene de computeEmployeePeriod, la misma función
// que alimenta el Resumen de Control de Personal. Este módulo se ocupa de
// congelar el mes, generar la boleta, registrar el pago y guardar el historial.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import {
    FiDollarSign, FiLock, FiUnlock, FiDownload, FiFileText, FiCheck, FiUpload,
    FiRefreshCw, FiTrash2, FiAlertTriangle, FiChevronLeft, FiChevronRight,
    FiClock, FiMinusCircle, FiUsers, FiExternalLink, FiSearch, FiX,
} from 'react-icons/fi';
import { computeEmployeePeriod, monthRange } from '../../services/payrollCalc';
import {
    setPayrollWorkspace, subscribeToPayrollRuns, savePayrollRun,
    closePayrollRun, reopenPayrollRun, deletePayrollRun,
    markItemPaid, unmarkItemPaid,
} from '../../services/payrollService';

const money = (n) => `S/ ${(Number(n) || 0).toFixed(2)}`;
const periodLabel = (p) => {
    try { return format(parseISO(p + '-01'), "MMMM 'de' yyyy", { locale: es }); }
    catch { return p; }
};

// ── Boleta en PDF ───────────────────────────────────────────────────────────
const buildBoleta = (item, period, companyName = 'Hingenia') => {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const M = 18;              // margen
    const W = 210;             // ancho A4
    let y = M;

    const line = (yy) => { pdf.setDrawColor(225); pdf.line(M, yy, W - M, yy); };
    const label = (text, x, yy) => {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(130);
        pdf.text(text.toUpperCase(), x, yy);
    };
    const value = (text, x, yy, bold = false) => {
        pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(10); pdf.setTextColor(30);
        pdf.text(String(text ?? '—'), x, yy);
    };
    const row = (text, amount, yy, opts = {}) => {
        pdf.setFont('helvetica', opts.bold ? 'bold' : 'normal');
        pdf.setFontSize(opts.bold ? 10.5 : 9.5);
        pdf.setTextColor(opts.color ? opts.color[0] : 60, opts.color ? opts.color[1] : 60, opts.color ? opts.color[2] : 60);
        pdf.text(text, M, yy);
        pdf.text(amount, W - M, yy, { align: 'right' });
    };

    // Cabecera
    pdf.setFillColor(2, 132, 199);
    pdf.rect(0, 0, W, 4, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(17); pdf.setTextColor(20);
    pdf.text('Boleta de pago', M, y + 6);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(120);
    pdf.text(companyName, W - M, y + 6, { align: 'right' });
    y += 11;
    pdf.setFontSize(11); pdf.setTextColor(60);
    pdf.text(periodLabel(period).replace(/^\w/, c => c.toUpperCase()), M, y);
    y += 6;
    line(y); y += 8;

    // Datos del colaborador
    label('Colaborador', M, y); label('DNI', M + 90, y); label('Cargo / profesión', M + 125, y);
    y += 5;
    value(item.userDisplayName, M, y, true);
    value(item.dni || '—', M + 90, y);
    value(item.profesion || '—', M + 125, y);
    y += 8;
    label('Banco', M, y); label('N° de cuenta', M + 45, y); label('CCI', M + 105, y);
    y += 5;
    value(item.banco || '—', M, y);
    value(item.numeroCuenta || '—', M + 45, y);
    value(item.cci || '—', M + 105, y);
    y += 8;
    line(y); y += 9;

    // Asistencia
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(20);
    pdf.text('Asistencia del periodo', M, y); y += 6;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5); pdf.setTextColor(80);
    pdf.text(`Días laborables: ${item.workDays}    ·    Días trabajados: ${item.daysWorked}    ·    Horas: ${(item.totalWorkedHrs || 0).toFixed(1)}h`, M, y);
    y += 5;
    pdf.text(`Tardanzas: ${item.totalLateMin} min    ·    Salidas tempranas: ${item.totalEarlyMin} min    ·    Exceso refrigerio: ${item.totalBreakExcessMin} min    ·    Permisos: ${item.totalPermitMin || 0} min    ·    Faltas: ${item.faltasCount}`, M, y);
    y += 8;
    line(y); y += 9;

    // Detalle económico
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(20);
    pdf.text('Detalle', M, y); y += 7;

    row('Sueldo del periodo', money(item.salary), y); y += 6;
    if (item.totalOTPay > 0) {
        row(`Horas extra (${(item.totalOTHours || 0).toFixed(2)}h)`, `+ ${money(item.totalOTPay)}`, y, { color: [21, 128, 61] });
        y += 6;
    }
    if (item.holidayPay > 0) {
        row(`Feriados trabajados (${item.holidaysWorkedCount || 0})`, `+ ${money(item.holidayPay)}`, y, { color: [21, 128, 61] });
        y += 6;
    }
    if (item.discount > 0) {
        row('Descuentos (tardanzas, refrigerio, permisos y faltas)', `- ${money(item.discount)}`, y, { color: [190, 40, 40] });
        y += 6;
    }
    y += 2; line(y); y += 8;

    pdf.setFillColor(240, 249, 255);
    pdf.roundedRect(M, y - 6, W - M * 2, 13, 2, 2, 'F');
    row('Neto a pagar', money(item.netPay), y + 2, { bold: true, color: [3, 105, 161] });
    y += 16;

    // Pie
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(150);
    pdf.text('Documento informativo generado por GestorPro. No constituye boleta de pago de ley.', M, 280);
    pdf.text(`Emitido el ${format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: es })}`, W - M, 280, { align: 'right' });

    return pdf;
};

// ── Modal de pago ───────────────────────────────────────────────────────────
const PayModal = ({ item, onClose, onConfirm }) => {
    const [file, setFile] = useState(null);
    const [reference, setReference] = useState('');
    const [saving, setSaving] = useState(false);

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-200">
                    <h3 className="text-[16px] font-extrabold text-gray-900">Registrar pago</h3>
                    <p className="text-[12.5px] text-gray-500 mt-0.5">{item.userDisplayName} · {money(item.netPay)}</p>
                </div>
                <div className="p-5 space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-[12px] text-gray-600 space-y-1">
                        <div className="flex justify-between"><span>Banco</span><b className="text-gray-800">{item.banco || '—'}</b></div>
                        <div className="flex justify-between"><span>Cuenta</span><b className="font-mono text-gray-800">{item.numeroCuenta || '—'}</b></div>
                        <div className="flex justify-between"><span>CCI</span><b className="font-mono text-gray-800">{item.cci || '—'}</b></div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">N° de operación (opcional)</label>
                        <input value={reference} onChange={e => setReference(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-400" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Comprobante (opcional)</label>
                        <label className="border-2 border-dashed border-gray-300 rounded-xl p-3 flex items-center gap-2 cursor-pointer hover:bg-gray-50">
                            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => setFile(e.target.files[0] || null)} />
                            <FiUpload size={15} className="text-gray-400" />
                            <span className="text-xs text-gray-600 truncate">{file ? file.name : 'Adjuntar imagen o PDF'}</span>
                        </label>
                    </div>
                </div>
                <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-[13px] bg-white border border-gray-200 rounded-xl font-semibold text-gray-700">Cancelar</button>
                    <button
                        onClick={async () => { setSaving(true); await onConfirm({ file, reference }); setSaving(false); }}
                        disabled={saving}
                        className="px-4 py-2 text-[13px] bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold disabled:opacity-50 flex items-center gap-1.5">
                        {saving ? 'Guardando...' : <><FiCheck size={14} /> Marcar pagado</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Página ──────────────────────────────────────────────────────────────────
const PayrollPage = ({
    currentUser, isAdmin, payrollUsers = [], employeeSettings = {}, globalSettings = {},
    attendanceRecords = [], overtimeRecords = [], allSolicitudes = [], users = [],
}) => {
    const [runs, setRuns] = useState([]);
    const [wsReady, setWsReady] = useState(false);
    const [period, setPeriod] = useState(() => format(new Date(), 'yyyy-MM'));
    const [payTarget, setPayTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const mountedRef = useRef(true);

    useEffect(() => () => { mountedRef.current = false; }, []);

    useEffect(() => {
        if (!currentUser?.uid) return;
        setPayrollWorkspace(currentUser.uid).then(() => { if (mountedRef.current) setWsReady(true); });
    }, [currentUser?.uid]);

    useEffect(() => {
        if (!wsReady) return;
        const unsub = subscribeToPayrollRuns(setRuns);
        return () => unsub();
    }, [wsReady]);

    const currentRun = useMemo(() => runs.find(r => r.period === period) || null, [runs, period]);
    const isClosed = currentRun?.status === 'cerrado';

    // Datos de RRHH del colaborador, para la boleta y la transferencia.
    const hrOf = (userId) => users.find(u => u.id === userId)?.hrDoc || {};

    // Cálculo en vivo del mes (borrador). Al cerrar se congela lo guardado.
    const liveItems = useMemo(() => {
        const { start, end } = monthRange(period);
        return payrollUsers
            .filter(u => employeeSettings[u.id])   // sólo quien tiene condiciones configuradas
            .map(u => {
                const r = computeEmployeePeriod({
                    user: u,
                    settings: employeeSettings[u.id] || {},
                    global: globalSettings,
                    attendanceRecords,
                    overtimeRecords,
                    solicitudes: allSolicitudes,
                    start, end, fullMonth: true,
                });
                const hr = hrOf(u.id);
                return {
                    userId: u.id,
                    userEmail: u.email,
                    userDisplayName: u.displayName || u.email,
                    userPhotoURL: u.photoURL || null,
                    dni: hr.dni || '', ruc: hr.ruc || '', profesion: hr.profesion || '',
                    banco: hr.banco || '', numeroCuenta: hr.numeroCuenta || '', cci: hr.cci || '',
                    salary: r.salary, hourlyRate: r.hourlyRate, workHrsPerDay: r.workHrsPerDay,
                    workDays: r.workDays, daysWorked: r.daysWorked,
                    totalWorkedHrs: Number((r.totalWorkedHrs || 0).toFixed(2)),
                    totalLateMin: r.totalLateMin, totalEarlyMin: r.totalEarlyMin,
                    totalBreakExcessMin: r.totalBreakExcessMin,
                    totalPermitMin: r.totalPermitMin,
                    faltasCount: r.faltasCount,
                    faltaDates: [...r.faltaRecords.map(x => x.date), ...r.detectedFaltaDates].sort(),
                    totalOTHours: Number((r.totalOTHours || 0).toFixed(2)),
                    totalOTPay: Number((r.totalOTPay || 0).toFixed(2)),
                    holidaysWorkedCount: (r.holidaysWorked || []).length,
                    holidayPay: Number((r.holidayPay || 0).toFixed(2)),
                    discount: Number((r.discount || 0).toFixed(2)),
                    netPay: Number((r.netPay || 0).toFixed(2)),
                    paid: false,
                };
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, payrollUsers, employeeSettings, globalSettings, attendanceRecords, overtimeRecords, allSolicitudes, users]);

    // Lo que se muestra: si el mes está cerrado manda lo guardado; si está en
    // borrador se recalcula, pero conservando las marcas de pago ya hechas.
    const items = useMemo(() => {
        if (isClosed) return currentRun.items || [];
        const paidMap = new Map((currentRun?.items || []).map(it => [it.userId, it]));
        return liveItems.map(it => {
            const prev = paidMap.get(it.userId);
            return prev?.paid ? { ...it, paid: true, paidAt: prev.paidAt, paidBy: prev.paidBy, proofUrl: prev.proofUrl, proofName: prev.proofName, reference: prev.reference } : it;
        });
    }, [isClosed, currentRun, liveItems]);

    const visibleItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(it => `${it.userDisplayName} ${it.userEmail}`.toLowerCase().includes(q));
    }, [items, search]);

    const totals = useMemo(() => items.reduce((acc, it) => ({
        salary: acc.salary + (it.salary || 0),
        discount: acc.discount + (it.discount || 0),
        overtime: acc.overtime + (it.totalOTPay || 0) + (it.holidayPay || 0),
        net: acc.net + (it.netPay || 0),
        paid: acc.paid + (it.paid ? (it.netPay || 0) : 0),
        paidCount: acc.paidCount + (it.paid ? 1 : 0),
    }), { salary: 0, discount: 0, overtime: 0, net: 0, paid: 0, paidCount: 0 }), [items]);

    const persist = async () => {
        setSaving(true);
        try {
            const id = await savePayrollRun({
                id: currentRun?.id, period, items, totals, createdBy: currentUser?.uid,
            });
            toast.success('Planilla guardada');
            return id;
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar la planilla');
            return null;
        } finally { setSaving(false); }
    };

    const handleClose = async () => {
        const pendientes = items.filter(it => !it.paid).length;
        const aviso = pendientes > 0 ? `\n\nQuedan ${pendientes} colaborador(es) sin marcar como pagados.` : '';
        if (!window.confirm(`¿Cerrar la planilla de ${periodLabel(period)}?${aviso}\n\nLos montos quedan congelados: aunque después se corrijan marcaciones, esta planilla no cambia.`)) return;
        const id = currentRun?.id || await persist();
        if (!id) return;
        try {
            await savePayrollRun({ id, period, items, totals, createdBy: currentUser?.uid });
            await closePayrollRun(id, currentUser?.uid);
            toast.success('Planilla cerrada');
        } catch (e) { console.error(e); toast.error('Error al cerrar'); }
    };

    const handleReopen = async () => {
        if (!currentRun) return;
        if (!window.confirm('¿Reabrir la planilla? Volverá a recalcularse desde la asistencia actual.')) return;
        try { await reopenPayrollRun(currentRun.id); toast.success('Planilla reabierta'); }
        catch { toast.error('Error al reabrir'); }
    };

    const handleDelete = async () => {
        if (!currentRun) return;
        if (!window.confirm(`¿Eliminar la planilla de ${periodLabel(period)}? Se pierden las marcas de pago y los comprobantes quedan huérfanos.`)) return;
        try { await deletePayrollRun(currentRun.id); toast.success('Planilla eliminada'); }
        catch { toast.error('Error al eliminar'); }
    };

    const handlePay = async ({ file, reference }) => {
        if (!payTarget) return;
        let runId = currentRun?.id;
        if (!runId) runId = await persist();   // marcar pago exige tener la corrida guardada
        if (!runId) return;
        try {
            await markItemPaid(runId, payTarget.userId, { file, paidBy: currentUser?.uid, reference });
            toast.success('Pago registrado');
            setPayTarget(null);
        } catch (e) { console.error(e); toast.error('Error al registrar el pago'); }
    };

    const handleUnpay = async (item) => {
        if (!currentRun?.id) return;
        if (!window.confirm(`¿Quitar la marca de pago de ${item.userDisplayName}?`)) return;
        try { await unmarkItemPaid(currentRun.id, item.userId); toast.success('Marca de pago retirada'); }
        catch { toast.error('Error'); }
    };

    const exportExcel = () => {
        const data = items.map(it => ({
            'Colaborador': it.userDisplayName,
            'DNI': it.dni, 'Banco': it.banco, 'Cuenta': it.numeroCuenta, 'CCI': it.cci,
            'Días laborables': it.workDays, 'Días trabajados': it.daysWorked,
            'Horas trabajadas': it.totalWorkedHrs,
            'Tardanzas (min)': it.totalLateMin,
            'Salida temprana (min)': it.totalEarlyMin,
            'Exceso refrigerio (min)': it.totalBreakExcessMin,
            'Permisos (min)': it.totalPermitMin || 0,
            'Faltas': it.faltasCount,
            'Sueldo': it.salary,
            'Horas extra (h)': it.totalOTHours,
            'Pago horas extra': it.totalOTPay,
            'Feriados trabajados': it.holidaysWorkedCount || 0,
            'Pago feriados': it.holidayPay || 0,
            'Descuentos': it.discount,
            'Neto a pagar': it.netPay,
            'Pagado': it.paid ? 'Sí' : 'No',
            'N° operación': it.reference || '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Planilla');
        XLSX.writeFile(wb, `Planilla_${period}.xlsx`);
    };

    const downloadBoleta = (item) => {
        try {
            buildBoleta(item, period).save(`Boleta_${item.userDisplayName.replace(/\s+/g, '_')}_${period}.pdf`);
        } catch (e) { console.error(e); toast.error('No se pudo generar la boleta'); }
    };

    const downloadAllBoletas = () => {
        if (items.length === 0) return;
        items.forEach((it, i) => setTimeout(() => downloadBoleta(it), i * 400));
        toast.success(`Generando ${items.length} boletas...`);
    };

    const shiftPeriod = (delta) => {
        const d = startOfMonth(parseISO(period + '-01'));
        setPeriod(format(subMonths(d, -delta), 'yyyy-MM'));
    };

    if (!isAdmin) {
        return <div className="p-10 text-center text-gray-400 text-sm">Sólo administradores.</div>;
    }

    return (
        <div className="p-6 lg:p-8 space-y-5">
            {/* Cabecera */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                        <FiDollarSign className="text-emerald-600" /> Nómina
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Planilla interna: sueldo del periodo menos descuentos más horas extra.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <button onClick={() => shiftPeriod(-1)} className="px-2.5 py-2 hover:bg-gray-50 text-gray-500"><FiChevronLeft size={15} /></button>
                        <span className="px-3 text-[13px] font-bold text-gray-800 capitalize min-w-[150px] text-center">{periodLabel(period)}</span>
                        <button onClick={() => shiftPeriod(1)} className="px-2.5 py-2 hover:bg-gray-50 text-gray-500"><FiChevronRight size={15} /></button>
                    </div>
                    {isClosed ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-bold">
                            <FiLock size={13} /> Cerrada
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-[12px] font-bold">
                            <FiUnlock size={13} /> Borrador
                        </span>
                    )}
                </div>
            </div>

            {/* Totales */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                    { label: 'Colaboradores', value: items.length, icon: FiUsers, color: 'text-gray-900' },
                    { label: 'Sueldos', value: money(totals.salary), icon: FiDollarSign, color: 'text-gray-900' },
                    { label: 'H. extra y feriados', value: `+ ${money(totals.overtime)}`, icon: FiClock, color: 'text-blue-600' },
                    { label: 'Descuentos', value: `- ${money(totals.discount)}`, icon: FiMinusCircle, color: 'text-red-600' },
                    { label: 'Neto a pagar', value: money(totals.net), icon: FiDollarSign, color: 'text-emerald-600' },
                ].map((c, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">
                            <c.icon size={11} /> {c.label}
                        </div>
                        <div className={`text-[19px] font-extrabold tabular-nums ${c.color}`}>{c.value}</div>
                    </div>
                ))}
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar colaborador..."
                        className="pl-9 pr-8 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-400 w-60" />
                    {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={14} /></button>}
                </div>
                <div className="flex-1" />
                <button onClick={exportExcel} disabled={items.length === 0}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-700 hover:border-emerald-300 disabled:opacity-40 flex items-center gap-1.5">
                    <FiDownload size={13} /> Excel
                </button>
                <button onClick={downloadAllBoletas} disabled={items.length === 0}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-700 hover:border-sky-300 disabled:opacity-40 flex items-center gap-1.5">
                    <FiFileText size={13} /> Boletas
                </button>
                {!isClosed ? (
                    <>
                        <button onClick={persist} disabled={saving || items.length === 0}
                            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-700 hover:border-gray-300 disabled:opacity-40 flex items-center gap-1.5">
                            <FiRefreshCw size={13} className={saving ? 'animate-spin' : ''} /> Guardar borrador
                        </button>
                        <button onClick={handleClose} disabled={items.length === 0}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[12.5px] font-bold disabled:opacity-40 flex items-center gap-1.5">
                            <FiLock size={13} /> Cerrar planilla
                        </button>
                    </>
                ) : (
                    <button onClick={handleReopen}
                        className="px-3 py-2 bg-white border border-amber-300 text-amber-700 rounded-xl text-[12.5px] font-bold hover:bg-amber-50 flex items-center gap-1.5">
                        <FiUnlock size={13} /> Reabrir
                    </button>
                )}
                {currentRun && (
                    <button onClick={handleDelete} className="px-2.5 py-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl" title="Eliminar planilla">
                        <FiTrash2 size={14} />
                    </button>
                )}
            </div>

            {isClosed && (
                <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <FiLock size={15} className="text-emerald-600 shrink-0 mt-0.5" />
                    <p className="text-[12.5px] text-emerald-800 font-medium">
                        Planilla cerrada. Los montos están congelados tal como se calcularon al cerrarla, aunque después se corrijan marcaciones.
                        Para recalcularla, reábrela.
                    </p>
                </div>
            )}

            {/* Tabla */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">
                                <th className="text-left px-5 py-3">Colaborador</th>
                                <th className="text-center px-3 py-3">Días</th>
                                <th className="text-right px-3 py-3">Sueldo</th>
                                <th className="text-right px-3 py-3 text-blue-700">H. Extra</th>
                                <th className="text-right px-3 py-3 text-red-600">Descuentos</th>
                                <th className="text-right px-3 py-3 text-emerald-700">Neto</th>
                                <th className="text-center px-3 py-3">Pago</th>
                                <th className="text-right px-5 py-3">Boleta</th>
                            </tr>
                        </thead>
                        <tbody className="text-[13px]">
                            {visibleItems.length === 0 ? (
                                <tr><td colSpan={8} className="text-center py-14 text-gray-400 text-xs italic">
                                    {items.length === 0
                                        ? 'Sin colaboradores con condiciones configuradas. Configura sueldo y horario en Ajustes.'
                                        : `Nadie coincide con "${search}"`}
                                </td></tr>
                            ) : visibleItems.map(it => (
                                <tr key={it.userId} className="border-b border-gray-100 hover:bg-gray-50/50">
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-3">
                                            {it.userPhotoURL
                                                ? <img src={it.userPhotoURL} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                                                : <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-700 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{it.userDisplayName.charAt(0).toUpperCase()}</div>}
                                            <div className="min-w-0">
                                                <div className="font-bold text-gray-900 truncate">{it.userDisplayName}</div>
                                                <div className="text-[10.5px] text-gray-400 truncate">
                                                    {it.banco ? `${it.banco} · ${it.numeroCuenta || 's/n'}` : 'sin datos bancarios'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3.5 text-center">
                                        <div className="font-bold text-gray-800 tabular-nums">{it.daysWorked}/{it.workDays}</div>
                                        {it.faltasCount > 0 && (
                                            <div className="text-[10px] font-bold text-red-600 flex items-center justify-center gap-0.5 mt-0.5">
                                                <FiAlertTriangle size={9} /> {it.faltasCount} falta{it.faltasCount > 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-3.5 text-right font-mono text-gray-700">{money(it.salary)}</td>
                                    <td className="px-3 py-3.5 text-right font-mono text-blue-600">
                                        {it.totalOTPay > 0 ? `+ ${money(it.totalOTPay)}` : (it.holidayPay > 0 ? null : <span className="text-gray-300">—</span>)}
                                        {it.holidayPay > 0 && <div className="text-[10.5px] text-amber-600">Feriados + {money(it.holidayPay)}</div>}
                                    </td>
                                    <td className="px-3 py-3.5 text-right font-mono text-red-600">
                                        {it.discount > 0 ? `- ${money(it.discount)}` : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-3 py-3.5 text-right font-mono font-extrabold text-emerald-700">{money(it.netPay)}</td>
                                    <td className="px-3 py-3.5 text-center">
                                        {it.paid ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10.5px] font-bold">
                                                    <FiCheck size={10} /> Pagado
                                                </span>
                                                <div className="flex items-center gap-1.5">
                                                    {it.proofUrl && (
                                                        <a href={it.proofUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:underline flex items-center gap-0.5">
                                                            <FiExternalLink size={9} /> ver
                                                        </a>
                                                    )}
                                                    {!isClosed && (
                                                        <button onClick={() => handleUnpay(it)} className="text-[10px] text-gray-400 hover:text-red-500">quitar</button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <button onClick={() => setPayTarget(it)}
                                                className="px-2.5 py-1 rounded-lg border border-emerald-300 text-emerald-700 text-[11px] font-bold hover:bg-emerald-50">
                                                Marcar pagado
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-5 py-3.5 text-right">
                                        <button onClick={() => downloadBoleta(it)} className="p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg" title="Descargar boleta PDF">
                                            <FiFileText size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {items.length > 0 && (
                            <tfoot>
                                <tr className="bg-gray-50 border-t-2 border-gray-200 text-[12.5px] font-extrabold">
                                    <td className="px-5 py-3 text-gray-700">Totales</td>
                                    <td />
                                    <td className="px-3 py-3 text-right font-mono text-gray-800">{money(totals.salary)}</td>
                                    <td className="px-3 py-3 text-right font-mono text-blue-600">+ {money(totals.overtime)}</td>
                                    <td className="px-3 py-3 text-right font-mono text-red-600">- {money(totals.discount)}</td>
                                    <td className="px-3 py-3 text-right font-mono text-emerald-700">{money(totals.net)}</td>
                                    <td className="px-3 py-3 text-center text-[11px] text-gray-500">{totals.paidCount}/{items.length}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            {/* Historial */}
            <div>
                <h2 className="text-[15px] font-extrabold text-gray-900 mb-3">Historial de planillas</h2>
                {runs.length === 0 ? (
                    <p className="text-sm text-gray-400">Todavía no has cerrado ninguna planilla.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {runs.map(r => {
                            const pagados = (r.items || []).filter(i => i.paid).length;
                            return (
                                <button key={r.id} onClick={() => setPeriod(r.period)}
                                    className={`text-left bg-white border rounded-2xl p-4 transition hover:shadow-sm ${r.period === period ? 'border-sky-400 ring-2 ring-sky-100' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-bold text-gray-900 capitalize text-[13.5px]">{periodLabel(r.period)}</span>
                                        <span className={`text-[9.5px] font-bold px-2 py-0.5 rounded-full ${r.status === 'cerrado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {r.status === 'cerrado' ? 'CERRADA' : 'BORRADOR'}
                                        </span>
                                    </div>
                                    <div className="text-[19px] font-extrabold text-gray-900 tabular-nums">{money(r.totals?.net)}</div>
                                    <div className="text-[11px] text-gray-400 mt-1">
                                        {(r.items || []).length} colaboradores · {pagados} pagados
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {payTarget && (
                <PayModal item={payTarget} onClose={() => setPayTarget(null)} onConfirm={handlePay} />
            )}
        </div>
    );
};

export default PayrollPage;
